import { getOutreachItems } from './firestore/crud';
import { OutreachCategory, Campaign } from '../../types/outreach';
import {
  getEnhancedMessageTrackingData,
  getMailgunCampaignStats,
  getSesTrackingAggregates,
} from './api/outreachApiClient';
import type {
  CampaignStats,
  EnhancedMessageTrackingData,
  SesTrackingDailyAggregate,
} from './types';
import { buildStatsFromTracking } from './engagementStatsFallback';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';
import { getDaysFromRange } from '../../utils/engagementAnalytics';
import {
  logCacheDecision,
  logEngagementEvent,
  logRawPayloadIfTarget,
  resolveCorrelationId,
  instrumentAsyncCall,
} from './engagementLogging';

class HistoricalAggregatesFetchError extends Error {
  statusCode?: number;
  campaignScope: string;
  timeRange: EngagementTimeRange;

  constructor(
    message: string,
    options: {
      statusCode?: number;
      campaignScope: string;
      timeRange: EngagementTimeRange;
    }
  ) {
    super(message);
    this.name = 'HistoricalAggregatesFetchError';
    this.statusCode = options.statusCode;
    this.campaignScope = options.campaignScope;
    this.timeRange = options.timeRange;
  }
}

const resolveStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const withStatus = error as {
    statusCode?: number;
    status?: number;
    response?: { status?: number };
  };

  return (
    withStatus.statusCode ??
    withStatus.response?.status ??
    withStatus.status
  );
};

const CACHE_TTL_MS = 60 * 1000;

interface CacheEntry<T> {
  timestamp: number;
  data: T;
}

let campaignCache: CacheEntry<Campaign[]> | null = null;
const statsCache = new Map<string, CacheEntry<CampaignStats>>();
const trackingCache = new Map<string, CacheEntry<EnhancedMessageTrackingData[]>>();
const historicalAggregateCache = new Map<string, CacheEntry<SesTrackingDailyAggregate[]>>();

export interface CampaignEngagementSnapshot {
  campaign: Campaign;
  stats: CampaignStats | null;
  tracking: EnhancedMessageTrackingData[];
}

export interface EngagementSnapshotOptions {
  includeStats?: boolean;
  includeTracking?: boolean;
  forceRefresh?: boolean;
  fallbackToTrackingStats?: boolean;
  correlationId?: string;
  campaignIds?: string[];
}

interface EngagementFetchContext {
  correlationId?: string;
}


const isCacheValid = (entry: CacheEntry<any> | null | undefined): boolean =>
  Boolean(entry && Date.now() - entry.timestamp < CACHE_TTL_MS);

export const fetchSavedCampaigns = async (
  forceRefresh = false,
  context: EngagementFetchContext = {}
): Promise<Campaign[]> => {
  const correlationId = resolveCorrelationId(context.correlationId);
  const cacheDecision = logCacheDecision({
    cacheName: 'campaignCache',
    correlationId,
    forceRefresh,
    cacheEntry: campaignCache ?? undefined,
    ttlMs: CACHE_TTL_MS,
  });

  if (cacheDecision.useCache && campaignCache) {
    logEngagementEvent({
      event: 'campaignCache.return.cached',
      correlationId,
      details: { campaignCount: campaignCache.data.length },
    });
    return campaignCache.data;
  }

  try {
    const campaigns = await getOutreachItems<Campaign>(
      OutreachCategory.CAMPAIGNS,
      { suppressErrors: false }
    );
    campaignCache = {
      timestamp: Date.now(),
      data: campaigns,
    };
    logEngagementEvent({
      event: 'campaignCache.refresh',
      correlationId,
      details: { campaignCount: campaigns.length },
    });
    return campaigns;
  } catch (error) {
    logEngagementEvent({
      event: 'campaignCache.fetch.error',
      level: 'warn',
      correlationId,
      details: {
        reason: error instanceof Error ? error.message : 'unknown',
      },
    });
    throw error instanceof Error
      ? error
      : new Error('Failed to fetch saved campaigns');
  }
};

export const fetchCampaignStatsSnapshot = async (
  campaignId: string,
  forceRefresh = false,
  context: EngagementFetchContext = {}
): Promise<CampaignStats | null> => {
  if (!campaignId) {
    return null;
  }

  const correlationId = resolveCorrelationId(context.correlationId);
  const cached = statsCache.get(campaignId);
  const cacheDecision = logCacheDecision({
    cacheName: 'statsCache',
    correlationId,
    campaignId,
    forceRefresh,
    cacheEntry: cached ?? undefined,
    ttlMs: CACHE_TTL_MS,
  });

  if (cacheDecision.useCache && cached) {
    return cached.data;
  }

  try {
    const stats = await instrumentAsyncCall({
      operation: 'getMailgunCampaignStats',
      correlationId,
      campaignId,
      details: {
        forceRefresh,
        provider: 'ses',
      },
      run: () =>
        getMailgunCampaignStats(campaignId, {
          provider: 'ses',
          forceRefresh,
        }),
    });

    if (stats) {
      statsCache.set(campaignId, {
        data: stats,
        timestamp: Date.now(),
      });
      logRawPayloadIfTarget({
        correlationId,
        campaignId,
        operation: 'stats',
        payload: stats,
      });
    }

    return stats ?? null;
  } catch (error) {
    logEngagementEvent({
      event: 'stats.fetch.fallback',
      level: 'warn',
      correlationId,
      campaignId,
      details: {
        reason: error instanceof Error ? error.message : 'unknown',
      },
    });
    return null;
  }
};

export const fetchCampaignTrackingSnapshot = async (
  campaignId: string,
  forceRefresh = false,
  context: EngagementFetchContext = {}
): Promise<EnhancedMessageTrackingData[]> => {
  if (!campaignId) {
    return [];
  }

  const correlationId = resolveCorrelationId(context.correlationId);
  const cached = trackingCache.get(campaignId);
  const cacheDecision = logCacheDecision({
    cacheName: 'trackingCache',
    correlationId,
    campaignId,
    forceRefresh,
    cacheEntry: cached ?? undefined,
    ttlMs: CACHE_TTL_MS,
  });

  if (cacheDecision.useCache && cached) {
    return cached.data;
  }

  try {
    const response = await instrumentAsyncCall({
      operation: 'getEnhancedMessageTrackingData',
      correlationId,
      campaignId,
      details: {
        forceRefresh,
        maxCacheAge: CACHE_TTL_MS,
      },
      run: () =>
        getEnhancedMessageTrackingData(campaignId, {
          forceRefresh,
          maxCacheAge: CACHE_TTL_MS,
        }),
    });

    const dataCandidate: any = response;
    const events: EnhancedMessageTrackingData[] = Array.isArray(
      dataCandidate?.data
    )
      ? dataCandidate.data
      : Array.isArray(dataCandidate)
        ? dataCandidate
        : [];

    trackingCache.set(campaignId, {
      data: events,
      timestamp: Date.now(),
    });

    logRawPayloadIfTarget({
      correlationId,
      campaignId,
      operation: 'tracking',
      payload: events,
    });

    return events;
  } catch (error) {
    logEngagementEvent({
      event: 'tracking.fetch.fallback',
      level: 'warn',
      correlationId,
      campaignId,
      details: {
        reason: error instanceof Error ? error.message : 'unknown',
      },
    });
    return [];
  }
};

export const fetchEngagementSnapshots = async (
  options: EngagementSnapshotOptions = {}
): Promise<CampaignEngagementSnapshot[]> => {
  const {
    includeStats = true,
    includeTracking = true,
    forceRefresh = false,
    fallbackToTrackingStats = true,
    correlationId: providedCorrelationId,
    campaignIds = [],
  } = options;

  const correlationId = resolveCorrelationId(providedCorrelationId);
  logEngagementEvent({
    event: 'engagementSnapshots.fetch.start',
    correlationId,
    details: {
      includeStats,
      includeTracking,
      forceRefresh,
      fallbackToTrackingStats,
    },
  });

  const campaigns = await fetchSavedCampaigns(forceRefresh, {
    correlationId,
  });
  logEngagementEvent({
    event: 'engagementSnapshots.savedCampaigns.loaded',
    correlationId,
    details: { campaignCount: campaigns.length },
  });
  const filteredCampaigns = campaignIds.length
    ? campaigns.filter((campaign) => campaignIds.includes(campaign.id))
    : campaigns;

  if (!filteredCampaigns.length) {
    logEngagementEvent({
      event: 'engagementSnapshots.fetch.empty',
      correlationId,
      details: {
        campaignFilter: campaignIds.length ? campaignIds : undefined,
      },
    });
    return [];
  }

  const snapshots = await Promise.all(
    filteredCampaigns.map(async (campaign) => {
      const statsPromise = includeStats
        ? fetchCampaignStatsSnapshot(campaign.id, forceRefresh, {
          correlationId,
        })
        : Promise.resolve<CampaignStats | null>(null);

      const trackingPromise = includeTracking
        ? fetchCampaignTrackingSnapshot(campaign.id, forceRefresh, {
          correlationId,
        })
        : null;

      let [statsResult, trackingResult] = await Promise.all([
        statsPromise,
        trackingPromise ?? Promise.resolve<EnhancedMessageTrackingData[]>([]),
      ]);

      if (
        includeStats &&
        !statsResult &&
        fallbackToTrackingStats &&
        !trackingPromise
      ) {
        logEngagementEvent({
          event: 'tracking.fetch.viaFallback',
          correlationId,
          campaignId: campaign.id,
          details: { forceRefresh },
        });
        trackingResult = await fetchCampaignTrackingSnapshot(
          campaign.id,
          forceRefresh,
          { correlationId }
        );
      }

      if (
        includeStats &&
        !statsResult &&
        fallbackToTrackingStats &&
        trackingResult.length
      ) {
        const derivedStats = buildStatsFromTracking(
          campaign.id,
          trackingResult
        );

        if (derivedStats) {
          logEngagementEvent({
            event: 'stats.derivedFromTracking',
            correlationId,
            campaignId: campaign.id,
            details: {
              derivedFromTrackingCount: trackingResult.length,
            },
          });
          statsResult = derivedStats;
          statsCache.set(campaign.id, {
            data: derivedStats,
            timestamp: Date.now(),
          });
        }
      }

      return {
        campaign,
        stats: statsResult,
        tracking: includeTracking ? trackingResult : [],
      };
    })
  );

  logEngagementEvent({
    event: 'engagementSnapshots.fetch.complete',
    correlationId,
    details: {
      snapshotCount: snapshots.length,
      includeStats,
      includeTracking,
      forceRefresh,
    },
  });

  return snapshots;
};

export const fetchCampaignScopedSnapshots = async (
  campaignId: string | undefined,
  options: EngagementSnapshotOptions = {}
): Promise<CampaignEngagementSnapshot[]> => {
  if (!campaignId) {
    return fetchEngagementSnapshots(options);
  }

  const { campaignIds, ...rest } = options;
  return fetchEngagementSnapshots({
    ...rest,
    campaignIds: [campaignId],
  });
};

export const clearEngagementCaches = () => {
  campaignCache = null;
  statsCache.clear();
  trackingCache.clear();
  historicalAggregateCache.clear();
};

const getHistoricalCacheKey = (timeRange: EngagementTimeRange, campaignId?: string) =>
  `historical-${timeRange}-${campaignId ?? 'all'}`;

export const fetchHistoricalTrackingAggregates = async (
  timeRange: EngagementTimeRange,
  campaignId?: string,
  forceRefresh = false,
  context: EngagementFetchContext = {}
): Promise<SesTrackingDailyAggregate[]> => {
  const correlationId = resolveCorrelationId(context.correlationId);
  const cacheKey = getHistoricalCacheKey(timeRange, campaignId);
  const cached = historicalAggregateCache.get(cacheKey);
  const cacheDecision = logCacheDecision({
    cacheName: 'historicalAggregateCache',
    correlationId,
    forceRefresh,
    cacheEntry: cached ?? undefined,
    ttlMs: CACHE_TTL_MS,
  });

  if (cacheDecision.useCache && cached) {
    return cached.data;
  }

  let campaignIds: string[] = [];
  if (campaignId) {
    campaignIds = [campaignId];
    logEngagementEvent({
      event: 'historicalAggregates.campaigns.scoped',
      correlationId,
      details: {
        campaignCount: campaignIds.length,
        campaignScope: campaignId,
        timeRange,
      },
    });
  } else {
    const campaigns = await fetchSavedCampaigns(forceRefresh, {
      correlationId,
    });
    logEngagementEvent({
      event: 'historicalAggregates.campaigns.loaded',
      correlationId,
      details: {
        campaignCount: campaigns.length,
        campaignScope: 'all',
        timeRange,
      },
    });
    campaignIds = campaigns.map((campaign) => campaign.id);
  }

  if (!campaignIds.length) {
    logEngagementEvent({
      event: 'historicalAggregates.fetch.empty',
      correlationId,
      details: { timeRange, campaignScope: campaignId ?? 'all' },
    });
    return [];
  }

  const days = getDaysFromRange(timeRange);
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (days - 1));
  startDate.setHours(0, 0, 0, 0);

  // Estimate a per-campaign limit big enough to cover the requested window without exhausting the API
  const perCampaignLimit = Math.min(
    Math.max(days * 50, 500),
    2000,
  );

  try {
    const result = await instrumentAsyncCall({
      operation: 'getSesTrackingAggregates',
      correlationId,
      details: {
        timeRange,
        perCampaignLimit,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        campaignScope: campaignId ?? 'all',
      },
      run: () =>
        getSesTrackingAggregates(
          campaignIds,
          {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            perCampaignLimit,
          },
        ),
    });

    const aggregates = result?.aggregates ?? [];
    historicalAggregateCache.set(cacheKey, {
      data: aggregates,
      timestamp: Date.now(),
    });

    logEngagementEvent({
      event: 'historicalAggregates.cache.updated',
      correlationId,
      details: {
        cacheKey,
        aggregateCount: aggregates.length,
        timeRange,
        campaignScope: campaignId ?? 'all',
      },
    });

    return aggregates;
  } catch (error) {
    const statusCode = resolveStatusCode(error);
    const normalizedError =
      error instanceof Error
        ? error
        : new Error('Failed to fetch historical aggregates');

    logEngagementEvent({
      event: 'historicalAggregates.fetch.fallback',
      level: 'warn',
      correlationId,
      details: {
        reason: normalizedError.message,
        statusCode,
        timeRange,
        campaignScope: campaignId ?? 'all',
      },
    });

    throw new HistoricalAggregatesFetchError(normalizedError.message, {
      statusCode,
      campaignScope: campaignId ?? 'all',
      timeRange,
    });
  }
};
